/* eslint-disable no-param-reassign */
import { NavigateFunction, useNavigate, useSearchParams } from "react-router-dom";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  UIEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Theme,
  CircularProgress,
  Switch,
} from "@mui/material";
import { LocationContext, SuccessAlertContext } from "App";
import { Search } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpIcon from "@mui/icons-material/Help";
import Notify from "config/notify";
import ERROR_MESSAGE from "constants/message";
import { checkAccessTokenExpired, getUserType } from "util/auth";
import useOrderList from "../hooks/useOrderList";
import OrderContainer from "../components/OrderContainer";
import OrderTable from "../components/OrderTable";
import OrderSearchDialog from "../components/OrderSearchDialog";
import useOrderSearch from "../hooks/useOrderSearch";
import { Order, OrderStatus, SearchApiParams } from "../types";
import {
  convertUTCToDate,
  extractNoDupplicationSupplier,
  localeStringDisZero,
  orderStatusToNumber,
  removeEmptyValues,
  toOrderCheck,
} from "../utils/common";
import {
  MultiTypography,
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellNarrow,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "../components/TableCellSmall";
import { localEnv, hasRepresentativeCd } from "../constants";
import useOrderStoreList from "../hooks/useOrderStoreList";
import DeliveryDialog from "../components/DeliveryDialog";
import CustomTooltip from "../components/CustomTooltip";

const limit = 10;
const styles: { [key: string]: SxProps } = {
  buttonContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    position: "relative",
  },
  leftButtonContainer: {
    position: "absolute",
    top: 0,
    left: 0,
  },
  activeButton: {
    backgroundColor: "secondary.main",
    color: "var(--white)",
    "&:hover": {
      backgroundColor: "secondary.light",
    },
  },
  supplierTopCol: {
    // textAlign: 'center',
    p: 0.5,
    borderBottom: "none",
    height: 58,
  },
  supplierBottomCol: {
    // textAlign: 'center',
    p: 0.5,
    top: 67,
  },
};

/**
 * ヘッダーを返す
 * @param onBack 戻る
 * @param setIsOrderSearch 検索モーダルを開閉
 * @param setIsDeliveryStatus 配送状況
 * @param navigate 画面遷移等に使用
 * @returns コンポーネント
 */
function HeaderBox(
  onBack: Function,
  setIsOrderSearch: Dispatch<SetStateAction<boolean>>,
  setIsDeliveryStatus: Dispatch<SetStateAction<boolean>>,
  navigate: NavigateFunction,
) {
  return (
    <Box sx={styles.buttonContainer}>
      <Box sx={styles.leftButtonContainer}>
        <Box mx={1}>
          <Button onClick={() => onBack()} variant="custom-outlined">
            <Box>＜ 戻る</Box>
          </Button>
        </Box>
      </Box>
      <Box mx={1}>
        <Button onClick={() => setIsDeliveryStatus(true)} variant="contained">
          <Box>配送状況確認</Box>
        </Button>
      </Box>
      <Box mx={1}>
        <Button onClick={() => setIsOrderSearch(true)} variant="contained">
          <Search />
          <Box>絞込検索</Box>
        </Button>
      </Box>
      {/* ToDo: 店舗履歴のみ出す件で削除したが、本来必要。 */}
      {/* <Box mx={1}>
        <Button onClick={() => navigate("/order/store/form")} variant="contained">
          <Box>注文作成</Box>
        </Button>
      </Box> */}
    </Box>
  );
}

/**
 * 店舗注文商品一覧
 * @returns JSX.Element
 */
export default function OrderStoreList() {
  const [isOrderSearch, setIsOrderSearch] = useState(false);
  const [isDeliveryStatus, setIsDeliveryStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert } = useContext(SuccessAlertContext);

  // 検索条件のstate
  const [
    {
      isRepresentativeCd,
      isCustomerOrder,
      shopName,
      orderStatus,
      orderReceiptStatus,
      storeOrderer,
      approver,
      orderStartDate,
      orderEndDate,
      companyCdField,
      companyName,
      centerCd,
      orderNumber,
      productCd,
      productName,
      brand,
      janCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      storehouseSupplierWithBlank,
      storehouse,
      supplier,
      companyCd,
    },
    {
      setIsRepresentativeCd,
      setIsCustomerOrder,
      setShopName,
      setOrderStatus,
      setOrderReceiptStatus,
      setStoreOrderer,
      setApprover,
      setOrderStartDate,
      setOrderEndDate,
      setCompanyCdField,
      setCompanyName,
      setCenterCd,
      setOrderNumber,
      setProductCd,
      setProductName,
      setBrand,
      setjanCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setClassification,
      setStorehouseSupplierWithBlank,
      setStorehouse,
      setSupplier,
      setCategory,
    },
    { classificationOptions, tyreOptions, wheelOptions, brandOptions, shopOptions },
    { isLoading: isOptionsLoading },
  ] = useOrderSearch(true);

  const [
    {
      total,
      orderList,
      isLoading: isOrderLoading,
      isMoreLoading,
      isFastLoading,
      fireMoreLoadEvent,
    },
    { setOrderList, get, getMore: getMoreOrderList, setFireMoreLoadEvent, setInit, setTotal },
  ] = useOrderStoreList(showAlert);
  const navigate = useNavigate();

  // 企業ユーザーでログインしているか
  const checkCompanyUser = () => companyCd !== undefined && companyCd !== "";
  const [targetShopCdList, setTargetShopCdList] = useState<string[]>([]); // 企業一覧の場合に選択した店舗を入れておく
  const [filteredTargetShopCdList, setFilteredTargetShopCdList] = useState<string[]>([]);

  // 対象の店舗を抽出
  const getTargetShopList = useCallback(
    () => filteredTargetShopCdList.map((tsCd) => shopOptions.filter((so) => so.shopCd === tsCd)[0]),
    [filteredTargetShopCdList, shopOptions],
  );

  const representativeParam = () => {
    if (hasRepresentativeCd.indexOf(isRepresentativeCd) === 2 ) return 0
    if (hasRepresentativeCd.indexOf(isRepresentativeCd) === 1 ) return 1
    return undefined;
  }

  // 現在の検索条件にあるデータを基にAPIのパラメータを作成
  const createGetListApiParams = (): SearchApiParams => {
    const params: Omit<SearchApiParams, "limit" | "offset"> = {
      screen_type: 10,
      sort_type: 1, // 注文日降順
      // is_customer_order: Number(isCustomerOrder), 客注絞込は店舗にはない
      is_representative: representativeParam(),
      order_status: orderStatus === "" ? '1,2,3,4,5,9' : `${orderStatusToNumber(orderStatus)}`,
      store_name: shopName,
      author: storeOrderer,
      approver,
      order_no: orderNumber,
      item_cd: productCd,
      item_name: productName,
      brand: brand ?? "",
      jan_cd: janCd,
      item_num: productNumber,
      tire_width: classification === "タイヤ" ? tyreWidth : undefined,
      tire_ratio: classification === "タイヤ" ? tyreRatio : undefined,
      tire_rim_size: classification === "タイヤ" ? tyreRimDiam : undefined,
      wheel_width: classification === "ホイール" ? wheelWidth : undefined,
      wheel_pcd: classification === "ホイール" ? wheelPcd : undefined,
      wheel_hole: classification === "ホイール" ? wheelHole : undefined,
      wheel_rim_size: classification === "ホイール" ? wheelRimDiam : undefined,
      class_cd: classificationOptions.some((co) => co.name === classification)
        ? classificationOptions.filter((co) => co.name === classification)[0].id
        : "",
      vendor_cd: supplier
        ? extractNoDupplicationSupplier(classificationOptions).filter(
            (so) => so.supplierName === supplier,
          )[0].supplierCd
        : "",
      order_date_start: convertUTCToDate(orderStartDate),
      order_date_end: convertUTCToDate(orderEndDate),
    };
    return removeEmptyValues(targetShopCdList.length >= 0
      ? {...params, ...{
        client_cds: targetShopCdList
          .map(tsc => shopOptions.find(so => so.shopCd === tsc)?.clientCd)
          .filter(cCd => cCd != null) as string[]}}
      : params);
    // return removeEmptyValues(params);
  };

  const handleSearch = async () => {
    setIsOrderSearch(false);
    const params = createGetListApiParams();
    await get(params);
  };

  const handleBack = () => navigate(-1);
  const tableHeadElement = () => (
    <>
      <TableRow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>ステータス</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>注文番号</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>商品コード</TableHeaderCellNarrow>
        {/* <TableHeaderCellNarrow sx={styles.supplierTopCol}>JANコード</TableHeaderCellNarrow> */}
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>仕入先名</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>大分類</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol} colSpan={2}>
          ブランド
        </TableHeaderCellNarrow>
        <TableHeaderCellWide sx={styles.supplierTopCol} colSpan={2}>
          商品名
        </TableHeaderCellWide>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>サイズ</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>品番</TableHeaderCellNarrow>
        {checkCompanyUser() && (
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>店舗名</TableHeaderCellNarrow>
        )}
      </TableRow>
      <TableRow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>客注</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>注文日</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>納品希望日</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>出荷日</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>出荷予定日</TableHeaderCellNarrow>
        {/* <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
          仕入先
          <br />
          入荷予定
          <CustomTooltip title={<>仕入先での入荷予定です。店舗の入荷予定ではありません。</>} arrow>
            <Box sx={{ cursor: "pointer" }}>
              <Typography
                component="span"
                sx={{ color: "var(--red-600)", position: "absolute", top: 0, right: 12 }}
              >
                <HelpIcon sx={{ fontSize: 18 }} />
              </Typography>
            </Box>
          </CustomTooltip>
        </TableHeaderCellNarrow> */}
        <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
          発注数
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
          納品数
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
          配送会社
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
          送り状番号
        </TableHeaderCellNarrow>
        <TableHeaderCellWide sx={styles.supplierBottomCol} colSpan={checkCompanyUser() ? 3 : 2}>
          備考
        </TableHeaderCellWide>
      </TableRow>
    </>
  );
  const tableBodyElement = () => (
    <>
      {orderList.map((order, index) => {
        const isZebraStripeRow = index % 2 === 1;
        return (
          <Fragment key={`${order.id}:${order.orderDate}`}>
            <TableRow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.orderStatus}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.orderNumber}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.productCd}
              </TableCellNarrow>
              {/* <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.janCd}
              </TableCellNarrow> */}
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.supplierName}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.classification}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow} colSpan={2}>
                {order.brand}
              </TableCellNarrow>
              <TableCellWide multi gray={isZebraStripeRow} colSpan={2}>
                {order.productName}
              </TableCellWide>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.size}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.productNumber}
              </TableCellNarrow>
              {checkCompanyUser() && (
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.shopName}
                </TableCellNarrow>
              )}
            </TableRow>
            <TableRow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.isCustomerOrder ? "〇" : "×"}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.orderDate}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.deliveryDate}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.shippingDate}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.expectedShippingDate}
              </TableCellNarrow>
              {/* <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.arrivalDate}
              </TableCellNarrow> */}
              <TableCellNarrow right multi gray={isZebraStripeRow}>
                {localeStringDisZero(order.orderCount)}
              </TableCellNarrow>
              <TableCellNarrow right multi gray={isZebraStripeRow}>
                {localeStringDisZero(order.deliveryCount)}
              </TableCellNarrow>
              <TableCellNarrow right multi gray={isZebraStripeRow}>
                {order.shippingCompanyName}
              </TableCellNarrow>
              <TableCellNarrow right multi gray={isZebraStripeRow}>
                {order.trackingNumber}
              </TableCellNarrow>
              {/* <TableCellNarrow right multi gray={isZebraStripeRow}>
                {localeStringDisZero(order.wholesalePrice)}
                <br />
                {`(${localeStringDisZero(order.wholesaleUnitPrice)})`}
              </TableCellNarrow>
              <TableCellNarrow right multi gray={isZebraStripeRow}>
                {localeStringDisZero(order.shopPrice)}
                <br />
                {`(${localeStringDisZero(order.shopUnitPrice)})`}
              </TableCellNarrow> */}
              <TableCellWide multi gray={isZebraStripeRow} colSpan={checkCompanyUser() ? 3 : 2}>
                {order.remarks}
              </TableCellWide>
            </TableRow>
          </Fragment>
        );
      })}
    </>
  );

  // スクロール処理
  const handleScroll = async (e: UIEvent<HTMLDivElement>) => {
    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading.current || isMoreLoading) {
      return;
    }
    // const scrollTop = window.scrollY;
    // const windowHeight = window.innerHeight;
    // const documentHeight = document.documentElement.scrollHeight;
    const { scrollTop } = e.currentTarget;
    const windowHeight = e.currentTarget.clientHeight;
    const documentHeight = e.currentTarget.scrollHeight;
    if (scrollTop + windowHeight + 10 >= documentHeight) {
      console.log("scroll event:");
      isFastLoading.current = true;
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  // const checkScrollable = () => document.documentElement.scrollHeight > window.innerHeight;

  // // 中のコンテンツが小さいとスクロールできず、無限ローディングさせることができないため、事前に+αで読んでおく。
  // useEffect(() => {
  //   const oLength = orderList.length;
  //   if (limit <= oLength && oLength < total && !isLoading && !isFastLoading.current) {
  //     if (!checkScrollable()) {
  //       (async() => {
  //         await getMoreOrderList(createGetListApiParams());
  //       })();
  //     }
  //   }
  // }, [orderList, isLoading]);

  // useEffect(() => {
  //   (async () => {
  //     setInit(true);
  //   })();
  //   window.addEventListener('scroll', handleScroll);
  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //   }
  // }, []);

  useEffect(() => {
    setTimeout(() => {
      (async () => {
        const userType = getUserType();
        // 店舗または得意先、企業でない場合はエラー
        if (!localEnv && !(userType === "C0002" || userType === "C0003" || userType === "C0006")) {
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: "店舗、企業、または得意先が設定されていません。再度ログインしてください。",
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else if (!localEnv && checkAccessTokenExpired()) {
          // 期限切れなら
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else {
          // 初期処理
          await get(createGetListApiParams());
          setInit(true);
        }
      })();
    }, 0);
  }, []);

  // ローディングの統一
  useEffect(() => {
    setIsLoading(isOrderLoading || isOptionsLoading);
  }, [isOrderLoading, isOptionsLoading]);

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      await getMoreOrderList(createGetListApiParams());
    })();
  }, [fireMoreLoadEvent]);

  return (
    <>
      <OrderContainer
        orderTitle="注文履歴一覧"
        OrderTable={
          <OrderTable
            searchLen={total}
            isAllLoaded={orderList.length >= total}
            tableHeadElement={() => tableHeadElement()}
            tableBodyElement={() => tableBodyElement()}
            isLoading={isLoading}
            isMoreLoading={isMoreLoading}
            handleScroll={handleScroll}
            limit={limit}
            total={total}
            orderListLength={0}
            isFastLoading={isFastLoading}
            getMoreOrderList={getMoreOrderList}
            createGetListApiParams={createGetListApiParams}
          />
        }
        HeaderBox={HeaderBox(handleBack, setIsOrderSearch, setIsDeliveryStatus, navigate)}
        isLoading={isLoading}
        helpPath="/resources/orders/manual_order_store.pdf"
      />
      {!isLoading && (
        <OrderSearchDialog
          open={isOrderSearch}
          onClose={() => setIsOrderSearch(false)}
          onSearch={handleSearch}
          isRepresentativeCd={isRepresentativeCd}
          isCustomerOrder={isCustomerOrder}
          shopName={shopName}
          orderStatus={orderStatus}
          orderReceiptStatus={orderReceiptStatus}
          storeOrderer={storeOrderer}
          approver={approver}
          orderStartDate={orderStartDate}
          orderEndDate={orderEndDate}
          companyCd={companyCdField}
          companyName={companyName}
          centerCd={centerCd}
          orderNumber={orderNumber}
          productCd={productCd}
          productName={productName}
          brand={brand}
          janCd={janCd}
          productNumber={productNumber}
          tyreWidth={tyreWidth}
          tyreRatio={tyreRatio}
          tyreRimDiam={tyreRimDiam}
          wheelWidth={wheelWidth}
          wheelPcd={wheelPcd}
          wheelHole={wheelHole}
          wheelRimDiam={wheelRimDiam}
          classification={classification}
          supplier={supplier}
          tyreOptions={tyreOptions}
          wheelOptions={wheelOptions}
          classificationOptions={classificationOptions}
          brandOptions={brandOptions}
          setIsRepresentativeCd={setIsRepresentativeCd}
          setIsCustomerOrder={setIsCustomerOrder}
          setShopName={setShopName}
          setOrderStatus={setOrderStatus}
          setOrderReceiptStatus={setOrderReceiptStatus}
          setStoreOrderer={setStoreOrderer}
          setApprover={setApprover}
          setOrderStartDate={setOrderStartDate}
          setOrderEndDate={setOrderEndDate}
          setCompanyCd={setCompanyCdField}
          setCompanyName={setCompanyName}
          setCenterCd={setCenterCd}
          setProductCd={setProductCd}
          setOrderNumber={setOrderNumber}
          setProductName={setProductName}
          setBrand={setBrand}
          setjanCd={setjanCd}
          setProductNumber={setProductNumber}
          setTyreWidth={setTyreWidth}
          setTyreRatio={setTyreRatio}
          setTyreRimDiam={setTyreRimDiam}
          setWheelWidth={setWheelWidth}
          setWheelPcd={setWheelPcd}
          setWheelHole={setWheelHole}
          setWheelRimDiam={setWheelRimDiam}
          setClassification={setClassification}
          setSupplier={setSupplier}
          setCategory={setCategory}
          screenType={companyCd ? "企業一覧" : "店舗一覧"}
          targetShopCdList={targetShopCdList}
          setTargetShopCdList={setTargetShopCdList}
          targetShopLengthLimit={10}
          shopOptions={shopOptions}
        />
      )}
      <DeliveryDialog open={isDeliveryStatus} setOpen={setIsDeliveryStatus} />
    </>
  );
}
